import React, { useState } from 'react';
/**
 * Expertise App - Interactive Skills & Tools Showcase
 * Multi-view app experience with visual tool display
 */
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Code, Palette, Users, Sparkles, Zap, ArrowRight, CheckCircle2, Star, TrendingUp } from 'lucide-react';
import { useTheme } from '../../core/contexts/ThemeContext';
type ViewMode = 'skills' | 'tools' | 'roles' | 'about';
export function ExpertiseApp() {
  const {
    theme: currentTheme
  } = useTheme();
  const [activeView, setActiveView] = useState<ViewMode>('skills');
  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);
  const isLightTheme = currentTheme === 'light';
  const isNeonTheme = currentTheme === 'neon';
  const isHighContrast = currentTheme === 'highContrast';
  const views = [{
    id: 'skills' as const,
    label: 'Skills',
    icon: Brain
  }, {
    id: 'tools' as const,
    label: 'Tools',
    icon: Code
  }, {
    id: 'roles' as const,
    label: 'Rollen',
    icon: Users
  }, {
    id: 'about' as const,
    label: 'Waarom',
    icon: Sparkles
  }];
  const skillCategories = [{
    id: 'ai',
    title: 'AI & Creative Systems',
    icon: Sparkles,
    color: 'from-rose-500 to-pink-600',
    expertise: 90,
    skills: [{
      name: 'AI Video Generation',
      level: 95
    }, {
      name: 'Prompt Engineering',
      level: 90
    }, {
      name: 'Character Systems',
      level: 85
    }, {
      name: 'Narrative Design',
      level: 88
    }]
  }, {
    id: 'dev',
    title: 'Development',
    icon: Code,
    color: 'from-emerald-500 to-teal-600',
    expertise: 85,
    skills: [{
      name: 'React & TypeScript',
      level: 90
    }, {
      name: 'Rapid Prototyping',
      level: 88
    }, {
      name: 'AI-Assisted Coding',
      level: 85
    }, {
      name: 'System Architecture',
      level: 82
    }]
  }, {
    id: 'ux',
    title: 'UX & Design',
    icon: Palette,
    color: 'from-purple-500 to-violet-600',
    expertise: 88,
    skills: [{
      name: 'User Research',
      level: 85
    }, {
      name: 'Interaction Design',
      level: 90
    }, {
      name: 'Design Systems',
      level: 88
    }, {
      name: 'Prototyping',
      level: 92
    }]
  }, {
    id: 'strategy',
    title: 'Strategy & Product',
    icon: Brain,
    color: 'from-blue-500 to-indigo-600',
    expertise: 82,
    skills: [{
      name: 'Product Thinking',
      level: 85
    }, {
      name: 'Concept Development',
      level: 88
    }, {
      name: 'Stakeholder Management',
      level: 80
    }, {
      name: 'Agile/Scrum',
      level: 82
    }]
  }];
  const toolCategories = [{
    category: 'AI & Video',
    tools: [{
      name: 'Veo 3',
      icon: '🎬',
      proficiency: 'Expert'
    }, {
      name: 'Kling AI',
      icon: '✨',
      proficiency: 'Expert'
    }, {
      name: 'Luma AI',
      icon: '🎥',
      proficiency: 'Expert'
    }, {
      name: 'Google Whisk',
      icon: '🎨',
      proficiency: 'Advanced'
    }, {
      name: 'Midjourney',
      icon: '🖼️',
      proficiency: 'Expert'
    }, {
      name: 'Runway ML',
      icon: '🎞️',
      proficiency: 'Expert'
    }, {
      name: 'Claude AI',
      icon: '🤖',
      proficiency: 'Expert'
    }, {
      name: 'ChatGPT',
      icon: '💬',
      proficiency: 'Expert'
    }]
  }, {
    category: 'Development',
    tools: [{
      name: 'React',
      icon: '⚛️',
      proficiency: 'Expert'
    }, {
      name: 'TypeScript',
      icon: '📘',
      proficiency: 'Expert'
    }, {
      name: 'Tailwind CSS',
      icon: '🎨',
      proficiency: 'Expert'
    }, {
      name: 'CSS',
      icon: '💅',
      proficiency: 'Expert'
    }, {
      name: 'Magic Patterns',
      icon: '✨',
      proficiency: 'Expert'
    }, {
      name: 'Framer Motion',
      icon: '🎭',
      proficiency: 'Advanced'
    }]
  }, {
    category: 'Design & UX',
    tools: [{
      name: 'Figma',
      icon: '🎯',
      proficiency: 'Expert'
    }, {
      name: 'Adobe XD',
      icon: '🎨',
      proficiency: 'Advanced'
    }, {
      name: 'After Effects',
      icon: '🎞️',
      proficiency: 'Advanced'
    }, {
      name: 'Miro',
      icon: '🗂️',
      proficiency: 'Advanced'
    }]
  }, {
    category: 'Character & Prompt Mastering',
    tools: [{
      name: 'Video Prompting',
      icon: '🎬',
      proficiency: 'Expert'
    }, {
      name: 'Image Prompting',
      icon: '🖼️',
      proficiency: 'Expert'
    }, {
      name: 'Character Design',
      icon: '👤',
      proficiency: 'Expert'
    }, {
      name: 'Consistency Control',
      icon: '🎯',
      proficiency: 'Advanced'
    }]
  }];
  const roles = [{
    title: 'AI Creative',
    icon: Sparkles,
    color: 'from-rose-500 to-pink-600',
    description: 'Vertaal concepten naar AI-gedreven verhalen',
    capabilities: ['Character-driven storytelling', 'Prompt engineering & optimization', 'Visual narrative design', 'Consistent AI generation']
  }, {
    title: 'UX Designer',
    icon: Palette,
    color: 'from-purple-500 to-violet-600',
    description: 'Ontwerp intuïtieve, mensgerichte ervaringen',
    capabilities: ['User research & testing', 'Interaction design', 'Design systems', 'Rapid prototyping']
  }, {
    title: 'Product Owner',
    icon: Users,
    color: 'from-blue-500 to-indigo-600',
    description: 'Breng visie, team en technologie samen',
    capabilities: ['Product strategy', 'Stakeholder alignment', 'Roadmap planning', 'Agile ceremonies']
  }, {
    title: 'System Thinker',
    icon: Brain,
    color: 'from-emerald-500 to-teal-600',
    description: 'Zie de samenhang tussen tools, mensen en processen',
    capabilities: ['Ecosystem design', 'Process optimization', 'Integration thinking', 'Scalable solutions']
  }];
  return <div className={`h-full flex flex-col ${isLightTheme ? 'bg-gradient-to-br from-white to-gray-100' : isHighContrast ? 'bg-black' : isNeonTheme ? 'bg-black' : 'bg-gradient-to-br from-gray-900 to-black'}`} data-id="element-158">
      {/* Toolbar */}
      <div className={`flex items-center justify-between px-4 md:px-6 py-3 md:py-4 backdrop-blur-xl border-b ${isLightTheme ? 'bg-white/95 border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-black/40 border-white/10'}`} data-id="element-159">
        {/* Left - Title */}
        <div className="flex items-center gap-2 md:gap-3" data-id="element-160">
          <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center ${isNeonTheme ? 'shadow-lg shadow-emerald-500/50' : ''}`} data-id="element-161">
            <Brain className="w-4 h-4 md:w-5 md:h-5 text-white" data-id="element-162" />
          </div>
          <div className="hidden sm:block" data-id="element-163">
            <div className={`text-sm md:text-base font-bold ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-164">
              Expertise
            </div>
            <div className={`text-xs ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/40'}`} data-id="element-165">
              Skills, Tools & Rollen
            </div>
          </div>
        </div>
        {/* Center - View Selector (Desktop) */}
        <div className={`hidden md:flex items-center gap-2 rounded-xl p-1 ${isLightTheme ? 'bg-gray-100' : isHighContrast ? 'bg-gray-100' : isNeonTheme ? 'bg-fuchsia-900/20' : 'bg-white/5'}`} data-id="element-166">
          {views.map(view => {
          const Icon = view.icon;
          const isActive = activeView === view.id;
          return <button key={view.id} onClick={() => setActiveView(view.id)} className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all ${isActive ? 'text-white' : isLightTheme ? 'text-gray-600 hover:text-gray-900' : isHighContrast ? 'text-black hover:text-gray-700' : isNeonTheme ? 'text-cyan-300 hover:text-cyan-100' : 'text-white/50 hover:text-white/80'}`} data-id="element-167">
                {isActive && <motion.div layoutId="activeExpertiseView" className={`absolute inset-0 rounded-lg ${isHighContrast ? 'bg-black' : isNeonTheme ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-500 shadow-lg shadow-fuchsia-500/50' : 'bg-gradient-to-r from-emerald-500 to-teal-600'}`} transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-168" />}
                <div className="relative z-10 flex items-center gap-2" data-id="element-169">
                  <Icon className="w-4 h-4" data-id="element-170" />
                  {view.label}
                </div>
              </button>;
        })}
        </div>
        {/* Right - Stats Badge */}
        <div className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg ${isLightTheme ? 'bg-gray-100' : isHighContrast ? 'bg-gray-100' : isNeonTheme ? 'bg-fuchsia-900/20 border border-fuchsia-500/30' : 'bg-white/5'}`} data-id="element-171">
          <Star className={`w-4 h-4 ${isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-400' : 'text-emerald-400'}`} data-id="element-172" />
          <span className={`text-sm font-medium ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-300' : 'text-white/80'}`} data-id="element-173">
            20+ Tools
          </span>
        </div>
      </div>
      {/* Mobile View Selector */}
      <div className={`md:hidden flex items-center gap-2 px-4 py-3 border-b overflow-x-auto ${isLightTheme ? 'bg-gray-100 border-gray-200' : isHighContrast ? 'bg-gray-100 border-black' : isNeonTheme ? 'bg-black border-fuchsia-500/30' : 'bg-black/20 border-white/10'}`} data-id="element-174">
        {views.map(view => {
        const Icon = view.icon;
        const isActive = activeView === view.id;
        return <button key={view.id} onClick={() => setActiveView(view.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${isActive ? isHighContrast ? 'bg-black text-white' : isNeonTheme ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/50' : 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white' : isLightTheme ? 'bg-gray-200 text-gray-600' : isHighContrast ? 'bg-white text-black border border-black' : isNeonTheme ? 'bg-fuchsia-900/20 text-cyan-300 border border-cyan-500/30' : 'bg-white/5 text-white/50'}`} data-id="element-175">
              <Icon className="w-4 h-4" data-id="element-176" />
              {view.label}
            </button>;
      })}
      </div>
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8" data-id="element-177">
        <AnimatePresence mode="wait" data-id="element-178">
          <motion.div key={activeView} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="max-w-7xl mx-auto" data-id="element-179">
            {/* Skills View */}
            {activeView === 'skills' && <div className="space-y-6" data-id="element-180">
                <div className="text-center mb-8" data-id="element-181">
                  <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-182">
                    Mijn Vaardigheden
                  </h2>
                  <p className={`${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-300' : 'text-white/60'}`} data-id="element-183">
                    Waar AI, design en development elkaar ontmoeten
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-6" data-id="element-184">
                  {skillCategories.map((category, index) => {
                const Icon = category.icon;
                const isHovered = hoveredSkill === category.id;
                return <motion.div key={category.id} initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: index * 0.1
                }} onHoverStart={() => setHoveredSkill(category.id)} onHoverEnd={() => setHoveredSkill(null)} className={`backdrop-blur-xl rounded-2xl p-6 border transition-all ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'} ${isHovered ? 'scale-102' : ''}`} data-id="element-185">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6" data-id="element-186">
                          <div className="flex items-center gap-3" data-id="element-187">
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center ${isNeonTheme ? 'shadow-lg shadow-fuchsia-500/50' : ''}`} data-id="element-188">
                              <Icon className="w-6 h-6 text-white" data-id="element-189" />
                            </div>
                            <div data-id="element-190">
                              <h3 className={`font-bold ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-191">
                                {category.title}
                              </h3>
                              <div className="flex items-center gap-2 mt-1" data-id="element-192">
                                <div className={`text-xs font-medium ${isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-400' : 'text-emerald-400'}`} data-id="element-193">
                                  {category.expertise}% Expertise
                                </div>
                              </div>
                            </div>
                          </div>
                          <TrendingUp className={`w-5 h-5 ${isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-400' : 'text-emerald-400'}`} data-id="element-194" />
                        </div>
                        {/* Skills List */}
                        <div className="space-y-3" data-id="element-195">
                          {category.skills.map(skill => <div key={skill.name} data-id="element-196">
                              <div className="flex items-center justify-between mb-2" data-id="element-197">
                                <span className={`text-sm ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/80'}`} data-id="element-198">
                                  {skill.name}
                                </span>
                                <span className={`text-xs font-medium ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-199">
                                  {skill.level}%
                                </span>
                              </div>
                              <div className={`h-2 rounded-full overflow-hidden ${isLightTheme ? 'bg-gray-200' : isHighContrast ? 'bg-gray-200' : isNeonTheme ? 'bg-fuchsia-900/20' : 'bg-white/10'}`} data-id="element-200">
                                <motion.div initial={{
                          width: 0
                        }} animate={{
                          width: `${skill.level}%`
                        }} transition={{
                          duration: 1,
                          delay: index * 0.1
                        }} className={`h-full rounded-full bg-gradient-to-r ${category.color}`} data-id="element-201" />
                              </div>
                            </div>)}
                        </div>
                      </motion.div>;
              })}
                </div>
              </div>}
            {/* Tools View */}
            {activeView === 'tools' && <div className="space-y-8" data-id="element-202">
                <div className="text-center mb-8" data-id="element-203">
                  <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-204">
                    Mijn Toolbox
                  </h2>
                  <p className={`${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-300' : 'text-white/60'}`} data-id="element-205">
                    De tools die ik dagelijks gebruik om te creëren
                  </p>
                </div>
                {toolCategories.map((category, catIndex) => <motion.div key={category.category} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: catIndex * 0.1
            }} data-id="element-206">
                    <h3 className={`text-lg font-bold mb-4 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-300' : 'text-white'}`} data-id="element-207">
                      {category.category}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-208">
                      {category.tools.map((tool, toolIndex) => <motion.div key={tool.name} initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: catIndex * 0.1 + toolIndex * 0.05
                }} whileHover={{
                  scale: 1.05,
                  y: -4
                }} className={`backdrop-blur-xl rounded-xl p-4 border text-center transition-all ${isLightTheme ? 'bg-white border-gray-200 hover:border-emerald-300' : isHighContrast ? 'bg-white border-2 border-black hover:border-gray-700' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/30 hover:border-cyan-500' : 'bg-white/5 border-white/10 hover:border-emerald-500/50'}`} data-id="element-209">
                          <div className="text-4xl mb-2" data-id="element-210">{tool.icon}</div>
                          <div className={`font-medium text-sm mb-1 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-211">
                            {tool.name}
                          </div>
                          <div className={`text-xs px-2 py-0.5 rounded-full inline-block ${tool.proficiency === 'Expert' ? isHighContrast ? 'bg-black text-white' : isNeonTheme ? 'bg-cyan-500/20 text-cyan-300' : 'bg-emerald-500/20 text-emerald-400' : isLightTheme ? 'bg-gray-200 text-gray-600' : isHighContrast ? 'bg-gray-200 text-black' : isNeonTheme ? 'bg-fuchsia-500/20 text-fuchsia-300' : 'bg-white/10 text-white/60'}`} data-id="element-212">
                            {tool.proficiency}
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>)}
              </div>}
            {/* Roles View */}
            {activeView === 'roles' && <div className="space-y-6" data-id="element-213">
                <div className="text-center mb-8" data-id="element-214">
                  <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-215">
                    Mijn Rollen
                  </h2>
                  <p className={`${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-300' : 'text-white/60'}`} data-id="element-216">
                    Hoe ik waarde toevoeg in verschillende contexten
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-6" data-id="element-217">
                  {roles.map((role, index) => {
                const Icon = role.icon;
                return <motion.div key={role.title} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.1
                }} whileHover={{
                  scale: 1.02,
                  y: -4
                }} className={`backdrop-blur-xl rounded-2xl p-6 border ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-218">
                        <div className="flex items-start gap-4 mb-4" data-id="element-219">
                          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${role.color} flex items-center justify-center flex-shrink-0 ${isNeonTheme ? 'shadow-lg shadow-fuchsia-500/50' : ''}`} data-id="element-220">
                            <Icon className="w-7 h-7 text-white" data-id="element-221" />
                          </div>
                          <div className="flex-1" data-id="element-222">
                            <h3 className={`text-xl font-bold mb-2 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-223">
                              {role.title}
                            </h3>
                            <p className={`text-sm ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-300' : 'text-white/70'}`} data-id="element-224">
                              {role.description}
                            </p>
                          </div>
                        </div>
                        <div className="space-y-2" data-id="element-225">
                          {role.capabilities.map(capability => <div key={capability} className="flex items-center gap-2" data-id="element-226">
                              <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-400' : 'text-emerald-400'}`} data-id="element-227" />
                              <span className={`text-sm ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/80'}`} data-id="element-228">
                                {capability}
                              </span>
                            </div>)}
                        </div>
                      </motion.div>;
              })}
                </div>
              </div>}
            {/* About View */}
            {activeView === 'about' && <div className="space-y-6" data-id="element-229">
                <div className="text-center mb-8" data-id="element-230">
                  <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-231">
                    Waarom Ik?
                  </h2>
                  <p className={`${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-300' : 'text-white/60'}`} data-id="element-232">
                    Wat maakt mijn aanpak uniek
                  </p>
                </div>
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className={`backdrop-blur-xl rounded-2xl p-8 border ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-233">
                  <div className="space-y-6" data-id="element-234">
                    <div data-id="element-235">
                      <h3 className={`text-xl font-bold mb-3 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-236">
                        Ecosysteem Denker
                      </h3>
                      <p className={`text-lg leading-relaxed ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/80'}`} data-id="element-237">
                        Ik ben niet alleen een designer, developer of AI-expert
                        — ik ben iemand die <strong data-id="element-238">ecosystemen bouwt</strong>.
                      </p>
                    </div>
                    <div className={`p-6 rounded-xl border ${isLightTheme ? 'bg-gray-50 border-gray-200' : isHighContrast ? 'bg-gray-100 border-black' : isNeonTheme ? 'bg-fuchsia-900/20 border-fuchsia-500/30' : 'bg-white/5 border-white/10'}`} data-id="element-239">
                      <p className={`leading-relaxed ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/80'}`} data-id="element-240">
                        Door mijn achtergrond in UX, product ownership en
                        AI-integratie zie ik niet alleen wat werkt, maar ook{' '}
                        <em data-id="element-241">waarom</em> het werkt en <em data-id="element-242">hoe</em> je het
                        schaalbaarder, begrijpelijker en menselijker kunt maken.
                      </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4" data-id="element-243">
                      {[{
                    icon: Sparkles,
                    title: 'AI-First',
                    desc: 'Integreer AI als creatieve partner'
                  }, {
                    icon: Zap,
                    title: 'Rapid Prototyping',
                    desc: 'Van idee naar werkend product in dagen'
                  }, {
                    icon: Users,
                    title: 'Mens-Centraal',
                    desc: 'Technologie dient de gebruiker'
                  }].map((item, index) => {
                    const Icon = item.icon;
                    return <motion.div key={item.title} initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: 0.2 + index * 0.1
                    }} className={`p-4 rounded-xl text-center ${isLightTheme ? 'bg-gray-50' : isHighContrast ? 'bg-gray-100' : isNeonTheme ? 'bg-fuchsia-900/20' : 'bg-white/5'}`} data-id="element-244">
                            <Icon className={`w-8 h-8 mx-auto mb-2 ${isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-400' : 'text-emerald-400'}`} data-id="element-245" />
                            <div className={`font-bold mb-1 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-246">
                              {item.title}
                            </div>
                            <div className={`text-sm ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-300' : 'text-white/60'}`} data-id="element-247">
                              {item.desc}
                            </div>
                          </motion.div>;
                  })}
                    </div>
                    <div className="flex items-center justify-center gap-3 pt-4" data-id="element-248">
                      <Sparkles className={`w-6 h-6 ${isNeonTheme ? 'text-fuchsia-400' : 'text-fuchsia-500'}`} data-id="element-249" />
                      <Zap className={`w-6 h-6 ${isNeonTheme ? 'text-cyan-400' : 'text-emerald-500'}`} data-id="element-250" />
                      <Users className={`w-6 h-6 ${isNeonTheme ? 'text-fuchsia-400' : 'text-purple-500'}`} data-id="element-251" />
                    </div>
                  </div>
                </motion.div>
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}